import posthog, { type PostHogConfig } from 'posthog-js';
import type { Shell } from '../shell/resolveShell';

const DEFAULT_API_HOST = 'https://us.i.posthog.com';

interface IdentifyArgs {
  userId: string;
  email?: string | null;
  displayName?: string | null;
  planCode: 'free' | 'pro' | 'team' | 'internal';
  shell: Shell;
}

interface ResolvedConfig {
  apiKey: string;
  apiHost: string;
}

let initialized = false;
let identifiedUserId: string | null = null;

function readConfig(): ResolvedConfig | null {
  const apiKey = import.meta.env.VITE_POSTHOG_PROJECT_KEY;
  if (typeof apiKey !== 'string' || apiKey.length === 0) return null;
  const apiHost =
    import.meta.env.VITE_POSTHOG_API_HOST?.trim() || DEFAULT_API_HOST;
  return { apiKey, apiHost };
}

function buildClientConfig(apiHost: string): Partial<PostHogConfig> {
  return {
    api_host: apiHost,
    capture_pageview: 'history_change',
    capture_exceptions: true,
    persistence: 'localStorage+cookie',
    disable_session_recording: false,
    session_recording: {
      maskAllInputs: true,
      maskTextSelector:
        '[data-posthog-mask], input[type=password], [data-secret], .secret-value',
    },
  };
}

/**
 * Boot PostHog once per page load. No-op when the project key is unset
 * (local dev or environments where analytics is intentionally off).
 *
 * Cloud vs self-host is a config decision, not a code one — flip
 * VITE_POSTHOG_API_HOST to the self-hosted URL when that lands.
 */
export function initPostHog(): boolean {
  if (initialized) return true;
  const config = readConfig();
  if (!config) return false;
  posthog.init(config.apiKey, buildClientConfig(config.apiHost));
  initialized = true;
  return true;
}

/**
 * Tag the current user. Idempotent — re-calling with the same userId is
 * a no-op so React-effect re-renders don't churn the SDK.
 */
export function identifyPostHogUser(args: IdentifyArgs): void {
  if (!initialized) return;
  if (identifiedUserId === args.userId) return;
  posthog.identify(args.userId, {
    email: args.email ?? undefined,
    display_name: args.displayName ?? undefined,
    plan_code: args.planCode,
    shell: args.shell,
  });
  posthog.group('shell', args.shell, { shell: args.shell });
  identifiedUserId = args.userId;
}

/** Drop the identified user (call on logout). */
export function resetPostHog(): void {
  if (!initialized) return;
  posthog.reset();
  identifiedUserId = null;
}

/**
 * Drop the identified user under impersonation (v1.11). Distinct from
 * ``resetPostHog`` only in caller intent — both call ``posthog.reset()``.
 * The named alias makes the impersonation-suppression site readable
 * without conflating it with the logout path.
 */
export function resetPostHogUser(): void {
  resetPostHog();
}

/**
 * Fire a named event with optional properties. No-op when PostHog has
 * not been initialised (local dev / no project key), so callers don't
 * need to gate on env-var presence.
 */
export function trackPostHogEvent(
  event: string,
  properties?: Record<string, unknown>,
): void {
  if (!initialized) return;
  posthog.capture(event, properties);
}

/** Test-only: reset internal singleton state. */
export function _resetPostHogModuleStateForTesting(): void {
  initialized = false;
  identifiedUserId = null;
}
